import React, { useState, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './BookingManagement.css';
import {
  getConfirmedBookings,
  cancelBooking,
  deleteBooking,
  getManualBlockedDates,
  getManualBlockedDateRows,
  getPendingBookingRequests,
  addManualBlockedDates,
  removeManualBlockedDates,
  saveConfirmedBooking,
  saveBookingRequest,
  markBookingRequestStatus,
  ConfirmedBooking,
  ManualBlockedDateRow,
  BookingRequest,
} from '../utils/bookingStorage';
import { supabase } from '../lib/supabase';
import { camps } from '../data/camps';
import { Camp, Booking } from '../types';
import { formatLocalYYYYMMDD } from '../utils/dateUtils';
import { generateConfirmationPDF } from '../utils/pdfGenerator';
import { DEPOSIT_PERCENT, BALANCE_DUE_DAYS } from '../constants/booking';
import { PAYMENT_DEFAULTS } from '../constants/payment';
import { isUserListedAdmin } from '../utils/adminAuth';
import BlockedDatesCalendar from './BlockedDatesCalendar';
import BookingOverviewCalendar from './BookingOverviewCalendar';
import { buildOverviewDaySets } from '../utils/overviewCalendar';

const BookingManagement: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loginError, setLoginError] = useState<string>('');
  const [accessNotice, setAccessNotice] = useState<string | null>(null);
  const [bookings, setBookings] = useState<ConfirmedBooking[]>([]);
  const [selectedCamp, setSelectedCamp] = useState<string>('all');
  const [showManualBlockForm, setShowManualBlockForm] = useState(false);
  const [manualBlockStart, setManualBlockStart] = useState<Date | null>(null);
  const [manualBlockEnd, setManualBlockEnd] = useState<Date | null>(null);
  const [manualBlockCamp, setManualBlockCamp] = useState<string>('all');
  const [manualBlockedDates, setManualBlockedDates] = useState<string[]>([]);
  const [manualBlockRows, setManualBlockRows] = useState<ManualBlockedDateRow[]>([]);
  const [pendingRequests, setPendingRequests] = useState<BookingRequest[]>([]);
  const [pendingLoadError, setPendingLoadError] = useState<string>('');
  const [bothCampsFor, setBothCampsFor] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [bookingsLoadError, setBookingsLoadError] = useState<string>('');

  // Payment details used on generated confirmation PDFs (remembered in this browser).
  const [payAccountName, setPayAccountName] = useState<string>(PAYMENT_DEFAULTS.accountName);
  const [payBsb, setPayBsb] = useState<string>(PAYMENT_DEFAULTS.bsb);
  const [payAccountNumber, setPayAccountNumber] = useState<string>(PAYMENT_DEFAULTS.accountNumber);
  const [payId, setPayId] = useState<string>(PAYMENT_DEFAULTS.payId);
  const [showPaymentDetails, setShowPaymentDetails] = useState<boolean>(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Load saved payment details once.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('soco_payment_details');
      if (raw) {
        const saved = JSON.parse(raw);
        // Only override the file defaults with non-empty saved values.
        if (saved.accountName) setPayAccountName(saved.accountName);
        if (saved.bsb) setPayBsb(saved.bsb);
        if (saved.accountNumber) setPayAccountNumber(saved.accountNumber);
        if (saved.payId) setPayId(saved.payId);
      }
    } catch (e) {
      console.warn('Could not read saved payment details', e);
    }
  }, []);

  // Persist payment details whenever they change.
  useEffect(() => {
    try {
      localStorage.setItem(
        'soco_payment_details',
        JSON.stringify({
          accountName: payAccountName,
          bsb: payBsb,
          accountNumber: payAccountNumber,
          payId,
        })
      );
    } catch (e) {
      console.warn('Could not save payment details', e);
    }
  }, [payAccountName, payBsb, payAccountNumber, payId]);

  useEffect(() => {
    const client = supabase;
    if (!client) return;

    const { data } = client.auth.onAuthStateChange(async (_event, session) => {
      if (!session?.user?.email) {
        setIsAuthenticated(false);
        return;
      }
      const ok = await isUserListedAdmin(session.user.email);
      if (!ok) {
        await client.auth.signOut();
        setIsAuthenticated(false);
        setAccessNotice(
          'This account is not listed as an admin. Add your email to the admin_users table in Supabase, using the same spelling as your login.'
        );
      } else {
        setIsAuthenticated(true);
        setAccessNotice(null);
      }
    });

    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadBookings();
      loadManualBlockedDates();
      loadPendingRequests();
    }
  }, [isAuthenticated]);

  const overviewByCamp = useMemo(
    () =>
      camps.map((camp) => ({
        camp,
        daySets: buildOverviewDaySets(
          camp.id,
          manualBlockRows,
          pendingRequests,
          bookings
        ),
        pending: pendingRequests.filter((r) => r.campId === camp.id),
      })),
    [manualBlockRows, pendingRequests, bookings]
  );

  const checkAuth = async () => {
    try {
      if (!supabase) {
        setIsLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        const ok = await isUserListedAdmin(session.user.email);
        if (ok) {
          setIsAuthenticated(true);
          setAccessNotice(null);
        } else {
          await supabase.auth.signOut();
          setIsAuthenticated(false);
          setAccessNotice(
            'This account is not listed as an admin. Add your email to the admin_users table in Supabase.'
          );
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setAccessNotice(null);

    try {
      if (!supabase) {
        setLoginError('Supabase not configured');
        return;
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setLoginError(error.message);
        return;
      }

      if (data.session?.user?.email) {
        const ok = await isUserListedAdmin(data.session.user.email);
        if (ok) {
          setIsAuthenticated(true);
        } else {
          await supabase.auth.signOut();
          setLoginError(
            'Access denied: this email is not in the admin_users table. Ask a site admin to add you in Supabase.'
          );
        }
      }
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : 'Login failed');
    }
  };

  const handleLogout = async () => {
    try {
      if (supabase) {
        await supabase.auth.signOut();
        setIsAuthenticated(false);
        setBookings([]);
        setManualBlockedDates([]);
      }
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const loadBookings = async () => {
    try {
      setBookingsLoadError('');
      const { bookings: allBookings, error } = await getConfirmedBookings();
      setBookings(allBookings);
      if (error) {
        setBookingsLoadError(error);
        console.error('Bookings load error:', error);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      setBookingsLoadError(error instanceof Error ? error.message : 'Failed to load bookings');
    }
  };

  const loadManualBlockedDates = async () => {
    try {
      const [dates, rows] = await Promise.all([
        getManualBlockedDates(),
        getManualBlockedDateRows(),
      ]);
      setManualBlockedDates(dates);
      setManualBlockRows(rows);
    } catch (error) {
      console.error('Error loading manual blocked dates:', error);
    }
  };

  const loadPendingRequests = async () => {
    try {
      setPendingLoadError('');
      const { requests, error } = await getPendingBookingRequests();
      setPendingRequests(requests);
      if (error) setPendingLoadError(error);
    } catch (error) {
      console.error('Error loading pending requests:', error);
      setPendingLoadError(
        error instanceof Error ? error.message : 'Failed to load pending applications'
      );
    }
  };

  const refreshOverviewData = async () => {
    await Promise.all([loadBookings(), loadManualBlockedDates(), loadPendingRequests()]);
  };

  const filteredBookings = bookings.filter(booking => {
    if (selectedCamp === 'all') return true;
    return booking.campId === selectedCamp;
  });

  const handleCancelBooking = async (bookingId: string) => {
    if (window.confirm('Are you sure you want to cancel this booking? The dates will become available again.')) {
      setIsSaving(true);
      const success = await cancelBooking(bookingId);
      if (success) {
        await refreshOverviewData();
      } else {
        alert('Failed to cancel booking. Please try again.');
      }
      setIsSaving(false);
    }
  };

  const handleDeleteBooking = async (bookingId: string) => {
    if (window.confirm('Are you sure you want to permanently delete this booking? This action cannot be undone.')) {
      setIsSaving(true);
      const success = await deleteBooking(bookingId);
      if (success) {
        await refreshOverviewData();
      } else {
        alert('Failed to delete booking. Please try again.');
      }
      setIsSaving(false);
    }
  };

  // e.g. Richard Young, check-in September 2026 -> "SCC2609RY"
  const buildPdfReference = (guestName: string, checkIn: Date): string => {
    const year = String(checkIn.getFullYear()).slice(-2);
    const month = String(checkIn.getMonth() + 1).padStart(2, '0');
    const nameParts = guestName.trim().split(/\s+/).filter(Boolean);
    const initials =
      nameParts.length >= 2
        ? nameParts[0][0] + nameParts[nameParts.length - 1][0]
        : (nameParts[0] || 'XX').slice(0, 2);
    return `SCC${year}${month}${initials.toUpperCase()}`;
  };

  interface PdfSource {
    guestName: string;
    guestEmail: string;
    guestPhone?: string;
    campId: string;
    campName: string;
    checkIn: Date;
    checkOut: Date;
    guests: number;
    totalPrice: number;
  }

  /**
   * Generates the confirmation PDF for `src`. If `otherCamp` is given (the
   * guest is booking both camps at once), the PDF shows a combined camp
   * name, a per-camp cost breakdown, and a total across both camps.
   */
  const handleGeneratePdf = (src: PdfSource, otherCamp?: Camp) => {
    const checkIn = new Date(src.checkIn);
    const checkOut = new Date(src.checkOut);
    const nights = Math.ceil(
      (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
    );
    const primaryCamp = camps.find((c) => c.id === src.campId);
    const otherTotal = otherCamp
      ? Math.round(nights * otherCamp.pricePerNight * 100) / 100
      : 0;
    const total = src.totalPrice + otherTotal;
    const depositAmount = Math.round(total * (DEPOSIT_PERCENT / 100) * 100) / 100;
    const balanceAmount = Math.round((total - depositAmount) * 100) / 100;
    const balanceDueDate = new Date(checkIn);
    balanceDueDate.setDate(balanceDueDate.getDate() - BALANCE_DUE_DAYS);
    const reference = buildPdfReference(src.guestName, checkIn);

    if (!payBsb.trim() && !payId.trim()) {
      setShowPaymentDetails(true);
      alert('Please add your bank/PayID details first (Payment details panel above) so they appear on the PDF.');
      return;
    }

    const doc = generateConfirmationPDF({
      bookingRef: reference,
      guestName: src.guestName,
      guestEmail: src.guestEmail,
      guestPhone: src.guestPhone || undefined,
      campName: otherCamp ? `${src.campName} & ${otherCamp.name} (Both camps)` : src.campName,
      location: otherCamp ? otherCamp.location || primaryCamp?.location : primaryCamp?.location,
      checkIn,
      checkOut,
      nights,
      guests: src.guests,
      total,
      costLines: otherCamp
        ? [
            {
              label: `${src.campName} - $${primaryCamp?.pricePerNight ?? ''} x ${nights} nights`,
              amount: src.totalPrice,
            },
            {
              label: `${otherCamp.name} - $${otherCamp.pricePerNight} x ${nights} nights`,
              amount: otherTotal,
            },
          ]
        : undefined,
      depositPercent: DEPOSIT_PERCENT,
      depositAmount,
      balanceAmount,
      balanceDueDate,
      bankAccountName: payAccountName.trim(),
      bankBsb: payBsb.trim(),
      bankAccountNumber: payAccountNumber.trim(),
      payId: payId.trim() || undefined,
      paymentReference: reference,
    });
    doc.save(`confirmation-${reference}.pdf`);
  };

  /**
   * For a confirmed booking, finds its linked "whole island" counterpart
   * (the matching booking on the other camp created via the "Also book..."
   * checkbox at confirmation time), if one exists.
   */
  const getCombinedCounterpart = (booking: ConfirmedBooking): ConfirmedBooking | undefined => {
    if (booking.id.includes('-both-')) {
      const primaryId = booking.id.slice(0, booking.id.indexOf('-both-'));
      return bookings.find((b) => b.id === primaryId);
    }
    return bookings.find((b) => b.id.startsWith(`${booking.id}-both-`));
  };

  const handleConfirmRequest = async (req: BookingRequest) => {
    const alsoBookOtherCamp = !!bothCampsFor[req.id];
    // With exactly two camps in the business, "the other camp" is unambiguous.
    // If a third camp is ever added, this should become an explicit picker instead.
    const otherCamp = alsoBookOtherCamp ? camps.find((c) => c.id !== req.campId) : undefined;

    const confirmMessage = otherCamp
      ? `Confirm this booking for ${req.guestName} on both ${req.campName} and ${otherCamp.name} (same dates)? Both camps' dates will be blocked.`
      : `Confirm this booking for ${req.guestName}? It will be added to Confirmed Bookings and the dates will be blocked.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }
    setIsSaving(true);
    const confirmed: ConfirmedBooking = {
      id: req.id,
      campId: req.campId,
      campName: req.campName,
      checkIn: req.checkIn,
      checkOut: req.checkOut,
      guests: req.guests,
      totalPrice: req.totalPrice,
      guestName: req.guestName,
      guestEmail: req.guestEmail,
      guestPhone: req.guestPhone || '',
      confirmedAt: new Date().toISOString(),
      status: 'confirmed',
    };
    const saved = await saveConfirmedBooking(confirmed);
    if (!saved.success) {
      alert(saved.error || 'Could not confirm the booking. Please try again.');
      setIsSaving(false);
      return;
    }

    let otherCampError: string | null = null;
    let otherCampConflict = false;
    if (otherCamp) {
      // Don't create a matching booking if the other camp already has a
      // confirmed booking overlapping these dates — that's a real double-
      // booking conflict the admin needs to resolve, not something to
      // paper over automatically.
      const hasConflict = bookings.some(
        (b) =>
          b.campId === otherCamp.id &&
          b.status === 'confirmed' &&
          req.checkIn < b.checkOut &&
          b.checkIn < req.checkOut
      );

      if (hasConflict) {
        otherCampConflict = true;
      } else {
        const nights = Math.ceil(
          (req.checkOut.getTime() - req.checkIn.getTime()) / (1000 * 60 * 60 * 24)
        );
        const otherBookingId = `${req.id}-both-${otherCamp.id}`;
        const otherTotalPrice = Math.round(nights * otherCamp.pricePerNight * 100) / 100;

        // The confirm RPC (save_approved_booking) only succeeds if a matching
        // *pending* booking_requests row already exists with the same id (it's
        // a security guard against arbitrary inserts). There's no enquiry for
        // the other camp, so create an identical pending request for it first,
        // then confirm that request.
        const otherRequest: Booking = {
          id: otherBookingId,
          campId: otherCamp.id,
          checkIn: req.checkIn,
          checkOut: req.checkOut,
          guests: req.guests,
          totalPrice: otherTotalPrice,
          guestName: req.guestName,
          guestEmail: req.guestEmail,
          guestPhone: req.guestPhone || '',
          specialRequests: req.specialRequests,
        };
        const requestCreated = await saveBookingRequest(otherRequest, otherCamp.name);

        if (!requestCreated.success) {
          otherCampError =
            requestCreated.error || `Could not create a matching booking request for ${otherCamp.name}.`;
        } else {
          const otherBooking: ConfirmedBooking = {
            id: otherBookingId,
            campId: otherCamp.id,
            campName: otherCamp.name,
            checkIn: req.checkIn,
            checkOut: req.checkOut,
            guests: req.guests,
            totalPrice: otherTotalPrice,
            guestName: req.guestName,
            guestEmail: req.guestEmail,
            guestPhone: req.guestPhone || '',
            confirmedAt: new Date().toISOString(),
            status: 'confirmed',
          };
          const otherSaved = await saveConfirmedBooking(otherBooking);
          if (!otherSaved.success) {
            otherCampError =
              otherSaved.error || `Could not create the matching booking for ${otherCamp.name}.`;
          }
        }
      }
    }

    await markBookingRequestStatus(req.id, 'approved');
    await refreshOverviewData();
    setBothCampsFor((prev) => {
      const next = { ...prev };
      delete next[req.id];
      return next;
    });

    if (otherCampConflict) {
      alert(
        `${req.campName} booking confirmed. ${otherCamp!.name} already has a confirmed booking overlapping these dates, so it was NOT automatically booked — check Confirmed Bookings for a scheduling conflict before offering the whole island.`
      );
    } else if (otherCampError) {
      alert(
        `${req.campName} booking confirmed, but the matching ${otherCamp!.name} booking failed: ${otherCampError}. You can block those dates manually if needed.`
      );
    }
    setIsSaving(false);
  };

  const handleDeclineRequest = async (req: BookingRequest) => {
    if (!window.confirm(`Decline and remove the enquiry from ${req.guestName}?`)) {
      return;
    }
    setIsSaving(true);
    const ok = await markBookingRequestStatus(req.id, 'declined');
    if (ok) {
      await refreshOverviewData();
    } else {
      alert('Could not update the enquiry. Please try again.');
    }
    setIsSaving(false);
  };

  const handleAddManualBlock = async () => {
    if (!manualBlockStart || !manualBlockEnd) {
      alert('Please select both start and end dates');
      return;
    }

    setIsSaving(true);
    const dates: string[] = [];
    const current = new Date(manualBlockStart);
    const end = new Date(manualBlockEnd);

    while (current <= end) {
      dates.push(formatLocalYYYYMMDD(current));
      current.setDate(current.getDate() + 1);
    }

    const success = await addManualBlockedDates(dates, manualBlockCamp);
    if (success) {
      await loadManualBlockedDates();
      await loadPendingRequests();
      setShowManualBlockForm(false);
      setManualBlockStart(null);
      setManualBlockEnd(null);
      alert(`Blocked ${dates.length} date(s) for ${manualBlockCamp === 'all' ? 'all camps' : camps.find(c => c.id === manualBlockCamp)?.name}`);
    } else {
      alert('Failed to block dates. Please try again.');
    }
    setIsSaving(false);
  };

  const handleRemoveManualBlock = async (date: string) => {
    if (window.confirm(`Remove block for ${date}?`)) {
      setIsSaving(true);
      const success = await removeManualBlockedDates([date]);
      if (success) {
        await loadManualBlockedDates();
        await loadPendingRequests();
      } else {
        alert('Failed to remove blocked date. Please try again.');
      }
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="booking-management-page">
        <div className="booking-management-container">
          <div className="loading-message">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="booking-management-page">
        <div className="booking-management-container">
          <div className="login-container">
            <h1>Admin Login</h1>
            <p className="subtitle">Please sign in to manage bookings</p>
            {accessNotice && (
              <div className="error-message" role="alert" style={{ marginBottom: '1rem' }}>
                {accessNotice}
              </div>
            )}
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="form-input"
                />
              </div>
              {loginError && (
                <div className="error-message">{loginError}</div>
              )}
              <button type="submit" className="login-button" disabled={isSaving}>
                {isSaving ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            {!supabase && (
              <div className="warning-message">
                Supabase is not configured. Please set up your environment variables.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-management-page">
      <div className="booking-management-container">
        <div className="management-header">
          <h1>Booking Management</h1>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
        <p className="subtitle">Manage confirmed bookings and manually block dates</p>

        {bookingsLoadError && (
          <div className="error-message" role="alert" style={{ marginBottom: '1rem' }}>
            Could not load bookings from the database: {bookingsLoadError}
            {' '}If this persists, confirm Row Level Security allows SELECT on <code>bookings</code> for all users
            (see <code>supabase/schema.sql</code> policy &quot;Anyone can read bookings&quot;).
          </div>
        )}

        <section className="overview-section" aria-labelledby="availability-overview-heading">
          <h2 id="availability-overview-heading">Availability overview</h2>
          <p className="overview-legend-key">
            Red = manual block · Orange = pending application · Teal = confirmed booking
          </p>
          {pendingLoadError && (
            <p className="error-message" role="alert" style={{ textAlign: 'left' }}>
              Pending applications: {pendingLoadError}. Run{' '}
              <code>supabase/migrations/20260216190000_booking_requests.sql</code> and{' '}
              <code>supabase/rpc_booking_requests.sql</code> in Supabase if the table is missing.
            </p>
          )}
          <div className="overview-camps-grid">
            {overviewByCamp.map(({ camp, daySets, pending }) => (
              <article key={camp.id} className="overview-camp-card" aria-label={`${camp.name} availability`}>
                <h3 className="overview-camp-title">{camp.name}</h3>
                <BookingOverviewCalendar daySets={daySets} compact />
                {pending.length > 0 && (
                  <div className="overview-pending-summary">
                    <h4>Pending ({pending.length})</h4>
                    <ul className="overview-pending-list">
                      {pending.map((req) => (
                        <li key={req.id}>
                          {req.guestName} · {req.checkIn.toLocaleDateString()} –{' '}
                          {req.checkOut.toLocaleDateString()} · ${req.totalPrice.toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>

        {/* Manual Date Blocking Section */}
        <div className="manual-block-section">
          <div className="section-header">
            <h2>Manual Date Blocking</h2>
            <button 
              onClick={() => setShowManualBlockForm(!showManualBlockForm)}
              className="toggle-button"
              disabled={isSaving}
            >
              {showManualBlockForm ? 'Cancel' : 'Block Dates'}
            </button>
          </div>

          {showManualBlockForm && (
            <div className="manual-block-form">
              <div className="form-group">
                <label>Camp (or All Camps):</label>
                <select 
                  value={manualBlockCamp} 
                  onChange={(e) => setManualBlockCamp(e.target.value)}
                  disabled={isSaving}
                >
                  <option value="all">All Camps</option>
                  {camps.map(camp => (
                    <option key={camp.id} value={camp.id}>{camp.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date:</label>
                  <DatePicker
                    selected={manualBlockStart}
                    onChange={(date: Date | null) => setManualBlockStart(date)}
                    dateFormat="MMMM d, yyyy"
                    className="date-picker-input"
                    disabled={isSaving}
                  />
                </div>
                <div className="form-group">
                  <label>End Date:</label>
                  <DatePicker
                    selected={manualBlockEnd}
                    onChange={(date: Date | null) => setManualBlockEnd(date)}
                    minDate={manualBlockStart || undefined}
                    dateFormat="MMMM d, yyyy"
                    className="date-picker-input"
                    disabled={isSaving}
                  />
                </div>
              </div>
              <button onClick={handleAddManualBlock} className="block-button" disabled={isSaving}>
                {isSaving ? 'Blocking...' : 'Block Dates'}
              </button>
            </div>
          )}

          <div className="blocked-dates-list">
            <h3>Manually blocked dates</h3>
            <BlockedDatesCalendar
              dates={Array.from(new Set(manualBlockedDates)).sort()}
              onRemoveDate={isSaving ? undefined : handleRemoveManualBlock}
            />
          </div>
        </div>

        {/* Payment details for confirmation PDFs */}
        <div className="payment-details-section">
          <div className="section-header">
            <h2>Payment details for PDFs</h2>
            <button
              onClick={() => setShowPaymentDetails(!showPaymentDetails)}
              className="toggle-button"
            >
              {showPaymentDetails ? 'Hide' : 'Edit'}
            </button>
          </div>
          <p className="subtitle" style={{ textAlign: 'left' }}>
            Shown on the confirmation PDFs you generate below. Saved in this browser only.
          </p>
          {showPaymentDetails && (
            <div className="manual-block-form">
              <div className="form-group">
                <label>Account name:</label>
                <input
                  className="form-input"
                  value={payAccountName}
                  onChange={(e) => setPayAccountName(e.target.value)}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>BSB:</label>
                  <input
                    className="form-input"
                    value={payBsb}
                    onChange={(e) => setPayBsb(e.target.value)}
                    placeholder="000-000"
                  />
                </div>
                <div className="form-group">
                  <label>Account number:</label>
                  <input
                    className="form-input"
                    value={payAccountNumber}
                    onChange={(e) => setPayAccountNumber(e.target.value)}
                    placeholder="00000000"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>PayID (optional):</label>
                <input
                  className="form-input"
                  value={payId}
                  onChange={(e) => setPayId(e.target.value)}
                  placeholder="email or mobile"
                />
              </div>
            </div>
          )}
        </div>

        {/* Pending Enquiries Section */}
        <div className="bookings-section">
          <div className="section-header">
            <h2>Pending Enquiries ({pendingRequests.length})</h2>
          </div>
          {pendingRequests.length === 0 ? (
            <div className="no-bookings">
              <p>No pending enquiries</p>
            </div>
          ) : (
            <div className="bookings-list">
              {pendingRequests.map((req) => {
                const checkIn = new Date(req.checkIn);
                const checkOut = new Date(req.checkOut);
                const nights = Math.ceil(
                  (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
                );
                const otherCamp = camps.find((c) => c.id !== req.campId);
                return (
                  <div key={req.id} className="booking-card">
                    <div className="booking-header">
                      <div>
                        <h3>{req.campName}</h3>
                        <p className="booking-id">Enquiry: {req.id}</p>
                      </div>
                      <span className="status-badge pending">Pending</span>
                    </div>
                    <div className="booking-details">
                      <div className="detail-group"><strong>Guest:</strong> {req.guestName}</div>
                      <div className="detail-group"><strong>Email:</strong> {req.guestEmail}</div>
                      {req.guestPhone && (
                        <div className="detail-group"><strong>Phone:</strong> {req.guestPhone}</div>
                      )}
                      <div className="detail-group">
                        <strong>Dates:</strong> {checkIn.toLocaleDateString()} - {checkOut.toLocaleDateString()}
                      </div>
                      <div className="detail-group">
                        <strong>Nights:</strong> {nights} | <strong>Guests:</strong> {req.guests}
                      </div>
                      <div className="detail-group"><strong>Est. total:</strong> ${req.totalPrice.toFixed(2)}</div>
                      {req.specialRequests && (
                        <div className="detail-group"><strong>Message:</strong> {req.specialRequests}</div>
                      )}
                    </div>
                    {otherCamp && (
                      <label className="both-camps-toggle">
                        <input
                          type="checkbox"
                          checked={!!bothCampsFor[req.id]}
                          onChange={(e) =>
                            setBothCampsFor((prev) => ({ ...prev, [req.id]: e.target.checked }))
                          }
                          disabled={isSaving}
                        />
                        Also book {otherCamp.name} for the same guest &amp; dates (whole-island booking)
                      </label>
                    )}
                    <div className="booking-actions">
                      <button
                        onClick={() => handleConfirmRequest(req)}
                        className="confirm-button"
                        disabled={isSaving}
                      >
                        Confirm Booking
                      </button>
                      <button
                        onClick={() =>
                          handleGeneratePdf(
                            req,
                            bothCampsFor[req.id] ? camps.find((c) => c.id !== req.campId) : undefined
                          )
                        }
                        className="pdf-button"
                        disabled={isSaving}
                      >
                        Confirmation PDF
                      </button>
                      <button
                        onClick={() => handleDeclineRequest(req)}
                        className="delete-button"
                        disabled={isSaving}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bookings List Section */}
        <div className="bookings-section">
          <div className="section-header">
            <h2>Confirmed Bookings</h2>
            <select 
              value={selectedCamp} 
              onChange={(e) => setSelectedCamp(e.target.value)}
              className="camp-filter"
              disabled={isSaving}
            >
              <option value="all">All Camps</option>
              {camps.map(camp => (
                <option key={camp.id} value={camp.id}>{camp.name}</option>
              ))}
            </select>
          </div>

          {filteredBookings.length === 0 ? (
            <div className="no-bookings">
              <p>No bookings found</p>
            </div>
          ) : (
            <div className="bookings-list">
              {filteredBookings.map(booking => {
                const checkIn = new Date(booking.checkIn);
                const checkOut = new Date(booking.checkOut);
                const nights = Math.ceil(
                  (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
                );
                return (
                  <div 
                    key={booking.id} 
                    className={`booking-card ${booking.status === 'cancelled' ? 'cancelled' : ''}`}
                  >
                    <div className="booking-header">
                      <div>
                        <h3>{booking.campName}</h3>
                        <p className="booking-id">ID: {booking.id}</p>
                      </div>
                      <span className={`status-badge ${booking.status}`}>
                        {booking.status === 'confirmed'
                          ? booking.paidAt
                            ? '✓ Paid'
                            : '✓ Confirmed (unpaid)'
                          : '✗ Cancelled'}
                      </span>
                    </div>

                    <div className="booking-details">
                      <div className="detail-group">
                        <strong>Guest:</strong> {booking.guestName}
                      </div>
                      <div className="detail-group">
                        <strong>Email:</strong> {booking.guestEmail}
                      </div>
                      {booking.guestPhone && (
                        <div className="detail-group">
                          <strong>Phone:</strong> {booking.guestPhone}
                        </div>
                      )}
                      <div className="detail-group">
                        <strong>Dates:</strong> {checkIn.toLocaleDateString()} - {checkOut.toLocaleDateString()}
                      </div>
                      <div className="detail-group">
                        <strong>Nights:</strong> {nights} | <strong>Guests:</strong> {booking.guests}
                      </div>
                      <div className="detail-group">
                        <strong>Total:</strong> ${booking.totalPrice.toFixed(2)}
                      </div>
                      <div className="detail-group">
                        <strong>Confirmed:</strong> {new Date(booking.confirmedAt).toLocaleDateString()}
                      </div>
                      {booking.specialRequests && (
                        <div className="detail-group">
                          <strong>Special Requests:</strong> {booking.specialRequests}
                        </div>
                      )}
                    </div>

                    <div className="booking-actions">
                      <button
                        onClick={() => {
                          const counterpart = getCombinedCounterpart(booking);
                          const otherCamp = counterpart
                            ? camps.find((c) => c.id === counterpart.campId)
                            : undefined;
                          handleGeneratePdf(booking, otherCamp);
                        }}
                        className="pdf-button"
                        disabled={isSaving}
                      >
                        Confirmation PDF
                      </button>
                      {booking.status === 'confirmed' && (
                        <button
                          onClick={() => handleCancelBooking(booking.id)}
                          className="cancel-button"
                          disabled={isSaving}
                        >
                          Cancel Booking
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteBooking(booking.id)}
                        className="delete-button"
                        disabled={isSaving}
                      >
                        Delete
                      </button>
                    </div>

                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingManagement;
